import { Reorder, useDragControls } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { DotsSixVertical, Eye, EyeSlash, ArrowCounterClockwise } from "@phosphor-icons/react";

function Row({ id, title, visible, onToggle }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls}
      className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-3 shadow-sm select-none"
      data-testid={`customise-row-${id}`}>
      <button onPointerDown={(e) => controls.start(e)} aria-label="Drag to reorder"
        data-testid={`customise-drag-${id}`}
        className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy/60 transition-colors touch-none">
        <DotsSixVertical size={20} weight="bold" />
      </button>
      <span className={`flex-1 text-sm font-medium ${visible ? "text-navy" : "text-navy/40 line-through"}`}>{title}</span>
      {visible
        ? <Eye size={16} className="text-positive shrink-0" />
        : <EyeSlash size={16} className="text-navy/30 shrink-0" />}
      <Switch checked={visible} onCheckedChange={() => onToggle(id)} data-testid={`customise-toggle-${id}`} />
    </Reorder.Item>
  );
}

export default function DashboardCustomise({ open, onClose, widgets, order, hidden, onChange, onReset }) {
  const titleOf = (id) => (widgets.find((w) => w.id === id)?.title) || id;
  const isVisible = (id) => !hidden.includes(id);

  const setOrder = (newOrder) => onChange({ order: newOrder, hidden });
  const toggle = (id) => {
    const next = hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id];
    onChange({ order, hidden: next });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[380px] sm:max-w-[380px] overflow-y-auto" data-testid="dashboard-customise-panel">
        <SheetHeader>
          <SheetTitle className="font-heading font-bold text-navy">Customise dashboard</SheetTitle>
          <SheetDescription className="text-navy/70">
            Drag to reorder, and toggle cards on or off. Your layout is saved to your account and follows you across devices.
          </SheetDescription>
        </SheetHeader>

        <Reorder.Group axis="y" values={order} onReorder={setOrder} className="space-y-2 mt-5">
          {order.map((id) => (
            <Row key={id} id={id} title={titleOf(id)} visible={isVisible(id)} onToggle={toggle} />
          ))}
        </Reorder.Group>

        <button onClick={onReset} data-testid="customise-reset"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-navy/60 hover:text-orangeink transition-colors">
          <ArrowCounterClockwise size={15} /> Reset to default
        </button>
      </SheetContent>
    </Sheet>
  );
}
